import React, { useState, useCallback, useEffect, useRef } from "react";
import { QuestionPreview } from "../../../stores/CoursePreviewStore/Types";
import { Enrolment } from "../../../stores/EnrolmentStore/Types";
import { displayPercentage } from "../helpers";

interface Props {
  handleShowSidebar: () => void;
  lesson: QuestionPreview;
  enrolment: Enrolment;
  handleShowAiChat: (e, val) => void;
}

const Navbar: React.FC<Props> = ({
  enrolment,
  lesson,
  handleShowSidebar,
  handleShowAiChat,
}) => {
  // The tools bar tucks away when you scroll down into the lesson -- the one
  // time none of it is any use -- and comes straight back the moment you scroll
  // up. The CSS for this was already here (.cb-sub.is-hidden), but nothing ever
  // put the class on: the old handler only ran on the way up, and reached for
  // #enrolment_navbar with jQuery, which is the id of the previous design's bar
  // and no longer in the page. So the bar never moved.
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);

  const onScroll = useCallback(() => {
    const y = window.scrollY;

    setHidden((wasHidden) => {
      // Near the top there is nothing to gain by hiding, and the bar
      // reappearing as you land back at the hero reads as a glitch.
      if (y < 70) return false;
      // A 6px deadband: without it, the rubber-banding at the end of a scroll
      // flickers the bar in and out.
      if (y > lastY.current + 6) return true;
      if (y < lastY.current - 6) return false;

      return wasHidden;
    });

    lastY.current = y;
  }, []);

  useEffect(() => {
    lastY.current = window.scrollY;
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  const percentageCompleted = displayPercentage(enrolment);
  const progressCircumference = 100.5;
  const progressOffset =
    progressCircumference - (percentageCompleted / 100) * progressCircumference;

  const navTitle = () => {
    if (["passed", "failed"].includes(enrolment?.state)) {
      return "Completed";
    } else {
      return (
        <>
          Chapter {(lesson?.topic?.position || 0) + 1} / Lesson{" "}
          {(lesson?.position || 0) + 1}
        </>
      );
    }
  };

  return (
    <>
      <div className={`cb-sub ${hidden ? "is-hidden" : ""}`} id="subBar">
        <div className="cb-sub__inner">
          <div className="cb-sub__left">
            <div
              className="sb-progress"
              role="img"
              aria-label={`Course progress ${percentageCompleted} percent`}
            >
              <svg
                width="36"
                height="36"
                viewBox="0 0 38 38"
                aria-hidden="true"
              >
                <circle className="track" cx="19" cy="19" r="16"></circle>
                <circle
                  className="meter"
                  cx="19"
                  cy="19"
                  r="16"
                  strokeDasharray={progressCircumference}
                  strokeDashoffset={progressOffset}
                ></circle>
              </svg>
              <span className="pct" aria-hidden="true">
                {percentageCompleted}%
              </span>
            </div>
            <button
              className="sb-chapter"
              id="chapterToggle"
              type="button"
              aria-expanded="false"
              aria-controls="chapterDrawer"
              aria-haspopup="dialog"
              onClick={() => handleShowSidebar()}
            >
              <span className="sb-chapter__label">{navTitle()}</span>
              <svg
                className="chev"
                aria-hidden="true"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="m6 9 6 6 6-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
          <div className="cb-sub__right">
            <a
              className="sb-pill"
              href={`/enrolments/${enrolment?.id}/resources`}
              target="_blank"
            >
              <svg aria-hidden="true">
                <use href="#i-books"></use>
              </svg>
              <span>Resources</span>
            </a>
            <a
              className="sb-pill sb-pill--lives"
              href={`/enrolments/${enrolment?.id}/lives`}
              target="_blank"
            >
              <svg aria-hidden="true">
                <use href="#i-heart"></use>
              </svg>
              <span>{enrolment?.lives_count} Lives</span>
            </a>
            <button
              className="sb-pill sb-pill--ai"
              id="aiToggle"
              aria-label="Ask Deep Thought AI"
              aria-haspopup="dialog"
              aria-controls="aiDrawer"
              onClick={(e) => handleShowAiChat(e, true)}
            >
              <svg className="spark" aria-hidden="true">
                <use href="#i-spark-sun"></use>
              </svg>
              AI
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
